/**
 * Composable for managing overlay trigger evaluation, activation, dismissal,
 * and auto-hide logic within the video player.
 */

import { computed, ref, watch, onUnmounted, type ComputedRef, type Ref } from 'vue'
import type { OverlayConfig } from '../../types/embed'
import type { PlayState } from '../../types/player'

/**
 * An overlay that has been triggered and is currently active.
 */
export interface ActiveOverlay {
  config: OverlayConfig
  activatedAt: number
}

interface UseOverlaysOptions {
  overlays: Ref<OverlayConfig[]>
  currentTime: Ref<number>
  duration: Ref<number>
  playState: Ref<PlayState>
  showHomePage: Ref<boolean>
  onPauseRequested?: () => void
  onResumeRequested?: () => void
}

interface UseOverlaysReturn {
  activeOverlays: ComputedRef<ActiveOverlay[]>
  dismiss: (uid: string) => void
  /** Whether a card may still come up: not closed, and not a `showOnce` card already shown. */
  mayShow: (overlay: OverlayConfig) => boolean
  /** Takes every card down and forgets which were shown and closed. */
  resetShownState: () => void
  /** What a playlist's next entry starts from — see the function. */
  resetForEntry: () => void
}

const TIME_TRIGGER_WINDOW = 1.0

export function useOverlays(options: UseOverlaysOptions): UseOverlaysReturn {
  const {
    overlays,
    currentTime,
    duration,
    playState,
    showHomePage,
    onPauseRequested,
    onResumeRequested,
  } = options

  const activatedUids = ref(new Set<string>())
  const dismissedUids = ref(new Set<string>())
  const shownUids = ref(new Set<string>())
  const autoHideTimers = new Map<string, ReturnType<typeof setTimeout>>()

  function mayShow(overlay: OverlayConfig): boolean {
    if (dismissedUids.value.has(overlay.uid)) return false
    return !(overlay.showOnce && shownUids.value.has(overlay.uid))
  }

  function shouldTrigger(overlay: OverlayConfig): boolean {
    if (!mayShow(overlay)) return false
    if (showHomePage.value) return false

    switch (overlay.triggerType) {
      case 'video_start':
        return playState.value === 'playing' && currentTime.value < TIME_TRIGGER_WINDOW

      case 'video_end':
        return playState.value === 'ended'

      case 'time':
        if (overlay.triggerTime == null) return false
        return (
          currentTime.value >= overlay.triggerTime &&
          currentTime.value < overlay.triggerTime + TIME_TRIGGER_WINDOW
        )

      case 'percent':
        if (overlay.triggerPercent == null || duration.value <= 0) return false
        {
          const currentPercent = (currentTime.value / duration.value) * 100
          return currentPercent >= overlay.triggerPercent
        }

      case 'pause':
        return playState.value === 'paused'

      default:
        return false
    }
  }

  function activate(overlay: OverlayConfig): void {
    activatedUids.value = new Set([...activatedUids.value, overlay.uid])
    shownUids.value = new Set([...shownUids.value, overlay.uid])

    if (overlay.pausesVideo) {
      onPauseRequested?.()
    }

    if (overlay.autoHideSeconds != null && overlay.autoHideSeconds > 0) {
      const timer = setTimeout(() => {
        deactivate(overlay.uid)
      }, overlay.autoHideSeconds * 1000)
      autoHideTimers.set(overlay.uid, timer)
    }
  }

  function deactivate(uid: string): void {
    const overlay = overlays.value.find((o) => o.uid === uid)
    activatedUids.value = new Set([...activatedUids.value].filter((u) => u !== uid))

    const timer = autoHideTimers.get(uid)
    if (timer) {
      clearTimeout(timer)
      autoHideTimers.delete(uid)
    }

    if (overlay?.pausesVideo) {
      const stillHasPausingOverlay = overlays.value.some(
        (o) => o.uid !== uid && o.pausesVideo && activatedUids.value.has(o.uid),
      )
      if (!stillHasPausingOverlay) {
        onResumeRequested?.()
      }
    }
  }

  function dismiss(uid: string): void {
    dismissedUids.value = new Set([...dismissedUids.value, uid])
    deactivate(uid)
  }

  function resetShownState(): void {
    shownUids.value = new Set()
    resetForEntry()
  }

  /**
   * Takes every card down and forgets which were closed, **but not which `showOnce` cards were
   * shown**: a card closed on one entry of a playlist comes back on the next, and one left up does
   * not sit over it, while "once" stays once per sitting — an organization's card is the same card
   * on every entry, and would otherwise come up on each of them.
   */
  function resetForEntry(): void {
    dismissedUids.value = new Set()
    activatedUids.value = new Set()
    for (const timer of autoHideTimers.values()) {
      clearTimeout(timer)
    }
    autoHideTimers.clear()
  }

  // Evaluate triggers on time and state changes
  watch(
    [currentTime, playState, showHomePage],
    () => {
      if (showHomePage.value) return

      for (const overlay of overlays.value) {
        const isActive = activatedUids.value.has(overlay.uid)

        if (!isActive && shouldTrigger(overlay)) {
          activate(overlay)
        }

        // Deactivate pause-triggered overlays when video resumes
        if (
          isActive &&
          overlay.triggerType === 'pause' &&
          playState.value === 'playing'
        ) {
          deactivate(overlay.uid)
        }
      }
    },
    { immediate: false },
  )

  // Render order: recommendations first (back), then CTA, then rating last (front/on top).
  // Later items in the array appear on top in the DOM.
  const OVERLAY_RENDER_ORDER: Record<string, number> = {
    recommendations: 0,
    custom_html: 1,
    info: 2,
    cta: 3,
    rating: 4,
  }

  const activeOverlays = computed<ActiveOverlay[]>(() => {
    return overlays.value
      .filter((o) => activatedUids.value.has(o.uid))
      .sort((a, b) => (OVERLAY_RENDER_ORDER[a.overlayType] ?? 50) - (OVERLAY_RENDER_ORDER[b.overlayType] ?? 50))
      .map((config) => ({
        config,
        activatedAt: Date.now(),
      }))
  })

  onUnmounted(() => {
    for (const timer of autoHideTimers.values()) {
      clearTimeout(timer)
    }
    autoHideTimers.clear()
  })

  return {
    activeOverlays,
    dismiss,
    mayShow,
    resetShownState,
    resetForEntry,
  }
}
